"use client";

import { CaretLeft, CaretRight, X } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import { useCallback, useEffect, useState } from "react";

import {
  summitGalleryDays,
  type SummitGalleryDay,
  type SummitGalleryPhoto,
} from "@/components/home/summit-gallery-data";
import { cn } from "@/lib/utils";

export type SummitGalleryTranslations = {
  title: string;
  photoOf: string;
  close: string;
  previous: string;
  next: string;
};

const defaultTranslations: SummitGalleryTranslations = {
  title: "Live from",
  photoOf: "{current} of {total}",
  close: "Close",
  previous: "Previous photo",
  next: "Next photo",
};

type SummitDayGalleryProps = {
  translations?: SummitGalleryTranslations;
};

export function SummitDayGallery({
  translations = defaultTranslations,
}: SummitDayGalleryProps) {
  const days = summitGalleryDays;
  // Default to the latest day so new photos show first.
  const [activeDayId, setActiveDayId] = useState(
    days[days.length - 1]?.id ?? 1,
  );
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [paused, setPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);

  const activeDay: SummitGalleryDay | undefined =
    days.find((d) => d.id === activeDayId) ?? days[0];
  const photos = activeDay?.photos ?? [];
  const t = translations;

  // Duplicate the strip so the marquee can loop seamlessly (0% → -50%).
  const loopPhotos = [...photos, ...photos];
  // ~2.2s per photo keeps a gentle pace even with 40+ images.
  const marqueeDuration = Math.max(photos.length * 2.2, 40);
  const isPaused = paused || lightboxIndex !== null;

  const openLightbox = (index: number) => setLightboxIndex(index % photos.length);
  const closeLightbox = () => setLightboxIndex(null);

  const goPrev = useCallback(() => {
    setLightboxIndex((i) =>
      i === null ? null : (i - 1 + photos.length) % photos.length,
    );
  }, [photos.length]);

  const goNext = useCallback(() => {
    setLightboxIndex((i) =>
      i === null ? null : (i + 1) % photos.length,
    );
  }, [photos.length]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    if (lightboxIndex === null) return;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeLightbox();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [lightboxIndex, goPrev, goNext]);

  if (!activeDay || photos.length === 0) return null;

  const counterLabel = t.photoOf
    .replace("{current}", String((lightboxIndex ?? 0) + 1))
    .replace("{total}", String(photos.length));

  return (
    <section
      id="summit-gallery"
      aria-label={`${t.title} ${activeDay.label}`}
      className="relative border-b border-zinc-200 bg-[#F8F7F4] py-16 transition-colors duration-500 dark:border-white/5 dark:bg-zinc-950 md:py-24"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10">
        <div className="mb-10 flex flex-col gap-4 md:mb-14 md:flex-row md:items-end md:justify-between md:gap-8">
          <div className="min-w-0">
            <h2 className="font-serif text-4xl leading-[0.95] tracking-tight text-black dark:text-white md:text-6xl lg:text-7xl">
              {t.title}{" "}
              <span className="italic text-brand-red dark:text-brand-gold">
                {activeDay.label}
              </span>
            </h2>

            {days.length > 1 && (
              <nav
                aria-label="Summit days"
                className="mt-5 flex items-center gap-1"
              >
                {days.map((day) => {
                  const active = day.id === activeDay.id;
                  return (
                    <button
                      key={day.id}
                      type="button"
                      onClick={() => {
                        setActiveDayId(day.id);
                        setLightboxIndex(null);
                      }}
                      className={cn(
                        "relative px-3 py-2 font-display text-sm font-semibold tracking-wide transition-colors",
                        active
                          ? "text-brand-red dark:text-brand-gold"
                          : "text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200",
                      )}
                      aria-current={active ? "true" : undefined}
                    >
                      {String(day.id).padStart(2, "0")}
                      {active && (
                        <span className="absolute inset-x-2 -bottom-px h-0.5 bg-brand-red dark:bg-brand-gold" />
                      )}
                    </button>
                  );
                })}
              </nav>
            )}
          </div>

          <p
            className="font-scripture shrink-0 text-4xl leading-none text-brand-red md:text-5xl lg:text-6xl dark:text-brand-gold"
            aria-hidden={false}
          >
            {activeDay.dateLabel}
          </p>
        </div>
      </div>

      {/* Full-bleed auto-scrolling cinema strip */}
      <div
        className="relative overflow-hidden"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onFocusCapture={() => setPaused(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setPaused(false);
          }
        }}
      >
        {reduceMotion ? (
          <div
            className="flex gap-3 overflow-x-auto px-6 pb-2 md:gap-4 md:px-10"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {photos.map((photo, index) => (
              <GalleryCard
                key={photo.src}
                photo={photo}
                index={index}
                onOpen={() => openLightbox(index)}
              />
            ))}
          </div>
        ) : (
          <div
            key={activeDay.id}
            className="summit-gallery-marquee flex w-max flex-nowrap gap-3 px-6 pb-2 md:gap-4 md:px-10"
            style={{
              // Duration scales with photo count so speed stays roughly constant.
              animationDuration: `${marqueeDuration}s`,
              animationPlayState: isPaused ? "paused" : "running",
            }}
          >
            {loopPhotos.map((photo, index) => (
              <GalleryCard
                key={`${photo.src}-${index}`}
                photo={photo}
                index={index % photos.length}
                onOpen={() => openLightbox(index % photos.length)}
              />
            ))}
          </div>
        )}

        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 w-8 bg-gradient-to-r from-[#F8F7F4] to-transparent dark:from-zinc-950 md:w-16"
        />
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-[#F8F7F4] to-transparent dark:from-zinc-950 md:w-16"
        />
      </div>

      <AnimatePresence>
        {lightboxIndex !== null && photos[lightboxIndex] && (
          <Lightbox
            photo={photos[lightboxIndex]}
            counterLabel={counterLabel}
            closeLabel={t.close}
            prevLabel={t.previous}
            nextLabel={t.next}
            onClose={closeLightbox}
            onPrev={goPrev}
            onNext={goNext}
          />
        )}
      </AnimatePresence>
    </section>
  );
}

function GalleryCard({
  photo,
  index,
  onOpen,
}: {
  photo: SummitGalleryPhoto;
  index: number;
  onOpen: () => void;
}) {
  // Staggered heights + portrait/landscape widths for a journal feel
  const heightClass = photo.portrait
    ? "h-[280px] sm:h-[340px] md:h-[420px]"
    : index % 3 === 0
      ? "h-[240px] sm:h-[300px] md:h-[360px]"
      : "h-[280px] sm:h-[340px] md:h-[420px]";

  const widthClass = photo.portrait
    ? "w-[200px] sm:w-[240px] md:w-[280px]"
    : "w-[300px] sm:w-[380px] md:w-[520px]";

  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "group relative shrink-0 snap-start overflow-hidden rounded-2xl bg-zinc-200 text-left shadow-sm outline-none ring-brand-red/40 transition-transform duration-500 hover:-translate-y-1 focus-visible:ring-2 dark:bg-zinc-800 dark:ring-brand-gold/40",
        heightClass,
        widthClass,
        // Drop the first card a little lower on desktop for rhythm
        index % 4 === 1 && "md:mt-8",
        index % 4 === 3 && "md:mt-4",
      )}
    >
      <Image
        src={photo.src}
        alt={photo.alt}
        fill
        sizes={photo.portrait ? "280px" : "(max-width: 768px) 380px, 520px"}
        className="object-cover transition-transform duration-700 group-hover:scale-[1.04]"
        loading={index < 4 ? "eager" : "lazy"}
      />
    </button>
  );
}

function Lightbox({
  photo,
  counterLabel,
  closeLabel,
  prevLabel,
  nextLabel,
  onClose,
  onPrev,
  onNext,
}: {
  photo: SummitGalleryPhoto;
  counterLabel: string;
  closeLabel: string;
  prevLabel: string;
  nextLabel: string;
  onClose: () => void;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <motion.div
      role="dialog"
      aria-modal="true"
      aria-label={photo.alt}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[100] flex flex-col bg-black/95"
      onClick={onClose}
    >
      <div className="flex items-center justify-between px-4 py-4 md:px-8">
        <p className="font-display text-xs font-semibold tracking-[0.18em] text-white/60 uppercase">
          {counterLabel}
        </p>
        <button
          type="button"
          onClick={onClose}
          aria-label={closeLabel}
          className="flex h-10 w-10 items-center justify-center rounded-full text-white/80 transition-colors hover:bg-white/10 hover:text-white"
        >
          <X size={22} weight="bold" />
        </button>
      </div>

      <div
        className="relative flex min-h-0 flex-1 items-center justify-center px-4 pb-8 md:px-16"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onPrev}
          aria-label={prevLabel}
          className="absolute left-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 md:left-6"
        >
          <CaretLeft size={22} weight="bold" />
        </button>

        <motion.div
          key={photo.src}
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="relative h-full max-h-[80vh] w-full max-w-5xl"
        >
          <Image
            src={photo.src}
            alt={photo.alt}
            fill
            priority
            sizes="(max-width: 1024px) 100vw, 1024px"
            className="object-contain"
          />
        </motion.div>

        <button
          type="button"
          onClick={onNext}
          aria-label={nextLabel}
          className="absolute right-2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-sm transition-colors hover:bg-white/20 md:right-6"
        >
          <CaretRight size={22} weight="bold" />
        </button>
      </div>
    </motion.div>
  );
}
